/* C2 — Conversas (Painel do Cliente).
 *
 * O espaço de trabalho de quem atende: três colunas — lista, conversa e
 * contexto. Variante clara do Nebula. Ligada aos endpoints reais
 * (`/api/conversations`, `/messages`, `/takeover`, `/close`).
 *
 * Responder, assumir/devolver e encerrar funcionam. Áudio da Sofia toca pelo
 * player nativo. A atualização em tempo real (polling de /api/updates) liga como
 * na tela Caixa atual; aqui a conversa recarrega ao selecionar e ao responder. */

import React, { useEffect, useRef, useState } from 'react';

import { api } from '../api/cliente';
import { relativo } from '../admin/formato';
import { Button, Icon, Tag } from '../admin/ui';
import { useRecurso } from '../estado/recurso';

const TOM_EMOCAO = { satisfeito: 'success', neutro: 'neutral', insatisfeito: 'danger' };

async function carregarTodasMensagens(id, sinal) {
  const itens = [];
  let cursor;

  do {
    const pagina = await api.mensagens(id, { limit: 100, cursor }, sinal);
    itens.push(...(pagina.itens || []));
    cursor = pagina.proximo;
  } while (cursor && !sinal?.aborted);

  return { itens, proximo: null };
}

function mesclarPaginaRecente(atual, recente) {
  const novos = recente?.itens || [];
  const idsNovos = new Set(novos.map((m) => m.id));
  const anteriores = (atual?.itens || []).filter((m) => !idsNovos.has(m.id));
  return { itens: [...novos, ...anteriores], proximo: null };
}

export default function Conversas() {
  const [filtroStatus, setFiltroStatus] = useState('open');
  const [busca, setBusca] = useState('');
  const [selId, setSelId] = useState(null);

  const recurso = useRecurso(
    (sinal) => api.conversas({ status: filtroStatus || undefined, q: busca || undefined }, sinal),
    [filtroStatus, busca],
  );
  const conversas = recurso.dados?.itens || [];

  // Tempo quase real: recarrega a lista em silêncio (via definirDados, sem
  // acender o estado de carregando) a cada poucos segundos.
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const r = await api.conversas({ status: filtroStatus || undefined, q: busca || undefined });
        recurso.definirDados(r);
      } catch { /* silencioso: o próximo ciclo tenta de novo */ }
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroStatus, busca]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,300px) minmax(0,1fr) minmax(0,290px)', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      {/* Coluna 1 — lista */}
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, borderRight: '1px solid var(--border-subtle)', background: 'var(--surface-sidebar)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 14, borderBottom: '1px solid var(--border-subtle)' }}>
          <span style={{ position: 'relative', display: 'flex' }}>
            <span style={{ position: 'absolute', left: 12, top: 0, bottom: 0, display: 'flex', alignItems: 'center', color: 'var(--text-muted)', pointerEvents: 'none' }}><Icon name="search" size={15} /></span>
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome ou telefone"
              style={{ width: '100%', height: 38, padding: '0 12px 0 34px', background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-control)', color: 'var(--text-primary)', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', outline: 'none' }}
            />
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            {[['open', 'Abertas'], ['closed', 'Encerradas'], ['', 'Todas']].map(([v, r]) => {
              const ativo = filtroStatus === v;
              return (
                <button key={r} type="button" onClick={() => setFiltroStatus(v)} style={{ flex: 1, padding: '6px 8px', border: '1px solid ' + (ativo ? 'transparent' : 'var(--border-default)'), borderRadius: 'var(--radius-control)', cursor: 'pointer', background: ativo ? 'var(--accent-soft)' : 'transparent', color: ativo ? 'var(--text-brand)' : 'var(--text-secondary)', font: 'var(--type-label)', fontWeight: 'var(--fw-semibold)' }}>{r}</button>
              );
            })}
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {recurso.carregando && !conversas.length ? (
            <p style={{ padding: 20, font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>Carregando…</p>
          ) : conversas.length === 0 ? (
            <p style={{ padding: 20, font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>Nenhuma conversa por aqui.</p>
          ) : (
            conversas.map((c) => {
              const ativo = c.id === selId;
              return (
                <button key={c.id} type="button" onClick={() => setSelId(c.id)} style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%', textAlign: 'left', cursor: 'pointer', padding: '12px 14px', border: 0, borderBottom: '1px solid var(--border-subtle)', background: ativo ? 'var(--surface-active)' : 'transparent', boxShadow: ativo ? 'inset 3px 0 0 var(--accent)' : 'none' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {c.emocao && TOM_EMOCAO[c.emocao] && <span style={{ width: 7, height: 7, borderRadius: '50%', background: `var(--${TOM_EMOCAO[c.emocao] === 'neutral' ? 'text-muted' : TOM_EMOCAO[c.emocao] === 'success' ? 'success' : 'danger'})`, flex: 'none' }} />}
                    <span style={{ flex: 1, minWidth: 0, font: 'var(--type-body-strong)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.contato || c.telefone?.split('@')[0] || 'Sem nome'}</span>
                    {c.pendentes > 0 && <span className="nb-tab" style={{ minWidth: 18, height: 18, padding: '0 5px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius-pill)', background: 'var(--accent)', color: '#fff', font: 'var(--type-caption)', fontWeight: 'var(--fw-bold)' }}>{c.pendentes}</span>}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ flex: 1, minWidth: 0, font: 'var(--type-caption)', color: c.presenca === 'composing' ? 'var(--text-brand)' : 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.presenca === 'composing' ? 'digitando…' : c.ultima_mensagem?.texto || '—'}
                    </span>
                    <span style={{ font: 'var(--type-caption)', color: 'var(--text-placeholder)', flex: 'none' }}>{relativo(c.ultima_mensagem?.em || c.aberta_em)}</span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Coluna 2 + 3 — conversa e contexto */}
      {selId ? (
        <Conversa key={selId} id={selId} aoMudar={recurso.recarregar} />
      ) : (
        <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--text-muted)', textAlign: 'center', background: 'var(--canvas)' }}>
          <Icon name="message-square" size={24} />
          <span style={{ font: 'var(--type-title)', color: 'var(--text-secondary)' }}>Escolha uma conversa</span>
          <span style={{ font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)' }}>A lista à esquerda mostra as conversas desta empresa.</span>
        </div>
      )}
    </div>
  );
}

function Conversa({ id, aoMudar }) {
  const detalhe = useRecurso((sinal) => api.conversa(id, sinal), [id]);
  const msgs = useRecurso((sinal) => carregarTodasMensagens(id, sinal), [id]);
  const mensagensRef = useRef(null);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    mensagensRef.current = msgs.dados;
  }, [msgs.dados]);

  // Recarrega as mensagens da conversa aberta em silêncio, para novas respostas
  // (da pessoa ou da IA) aparecerem sem a pessoa ter de recarregar.
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const recentes = await api.mensagens(id, { limit: 100 });
        const todas = mesclarPaginaRecente(mensagensRef.current, recentes);
        mensagensRef.current = todas;
        msgs.definirDados(todas);
      } catch { /* silencioso */ }
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const c = detalhe.dados;
  const mensagens = [...(msgs.dados?.itens || [])].reverse();
  const humano = c?.congelado_ate && new Date(c.congelado_ate).getTime() > Date.now();

  async function enviar() {
    const t = texto.trim();
    if (!t) return;
    setEnviando(true);
    try {
      await api.responder(id, t);
      setTexto('');
      msgs.recarregar();
      aoMudar?.();
    } finally {
      setEnviando(false);
    }
  }

  async function alternarHumano() {
    if (humano) await api.devolver(id);
    else await api.assumir(id, 4);
    detalhe.recarregar();
  }

  async function encerrar() {
    await api.encerrar(id);
    detalhe.recarregar();
    aoMudar?.();
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--canvas)' }}>
        {/* cabeçalho da conversa */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '0 0 auto', padding: '12px 18px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-sidebar)' }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <span style={{ font: 'var(--type-h3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c?.contato || c?.telefone?.split('@')[0] || 'Conversa'}</span>
            <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>{c?.telefone?.split('@')[0]}{c?.presenca === 'composing' ? ' · digitando…' : ''}</span>
          </div>
          {c?.jornada && <Tag tone="info">{c.jornada.nome} · dia {c.jornada.dia}/{c.jornada.dias_total}</Tag>}
          <Tag tone={humano ? 'warning' : 'success'} dot>{humano ? 'Você assumiu' : 'IA ativa'}</Tag>
        </div>

        {/* mensagens */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {msgs.carregando && !mensagens.length ? (
            <span style={{ font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>Carregando mensagens…</span>
          ) : mensagens.map((m) => <Balao key={m.id} m={m} convId={id} />)}
        </div>

        {/* composer */}
        <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-end', gap: 10, padding: '12px 16px', borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-sidebar)' }}>
          <textarea
            rows={1}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
            placeholder={humano ? 'Escreva sua resposta…' : 'Assuma a conversa para escrever'}
            disabled={!humano}
            style={{ flex: 1, resize: 'none', maxHeight: 120, padding: '10px 12px', background: humano ? 'var(--surface-raised)' : 'var(--surface-sunken)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-control)', color: 'var(--text-primary)', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', lineHeight: 'var(--lh-normal)', outline: 'none' }}
          />
          <Button variant="primary" icon="send" onClick={enviar} disabled={!humano || enviando || !texto.trim()} style={{ height: 42 }}>{enviando ? '…' : 'Enviar'}</Button>
        </div>
      </div>

      {/* Coluna 3 — contexto */}
      <aside style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto', borderLeft: '1px solid var(--border-subtle)', background: 'var(--surface-sidebar)', padding: 18, gap: 20 }}>
        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h4 style={rotulo}>Contato</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '84px minmax(0,1fr)', gap: '8px 10px', fontSize: 'var(--fs-body-sm)' }}>
            <span style={{ color: 'var(--text-muted)' }}>Nome</span><span style={{ color: 'var(--text-primary)' }}>{c?.contato || '—'}</span>
            <span style={{ color: 'var(--text-muted)' }}>Telefone</span><span style={{ color: 'var(--text-primary)' }}>{c?.telefone?.split('@')[0] || '—'}</span>
            <span style={{ color: 'var(--text-muted)' }}>Aberta</span><span style={{ color: 'var(--text-primary)' }}>{relativo(c?.aberta_em)}</span>
          </div>
        </section>

        {c?.jornada && (
          <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <h4 style={rotulo}>Jornada de oração</h4>
            <div style={{ padding: 12, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-control)' }}>
              <span style={{ font: 'var(--type-body-strong)', fontSize: 'var(--fs-body-sm)' }}>{c.jornada.nome}</span>
              <div style={{ marginTop: 8, height: 6, borderRadius: 'var(--radius-pill)', background: 'var(--border-subtle)', overflow: 'hidden' }}>
                <span style={{ display: 'block', height: '100%', width: `${(c.jornada.dia / c.jornada.dias_total) * 100}%`, background: 'var(--accent)' }} />
              </div>
              <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)', marginTop: 6, display: 'block' }}>Dia {c.jornada.dia} de {c.jornada.dias_total}</span>
            </div>
          </section>
        )}

        <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <h4 style={rotulo}>Controle</h4>
          <Button variant={humano ? 'secondary' : 'primary'} icon={humano ? 'corner-up-left' : 'user-round'} onClick={alternarHumano}>{humano ? 'Devolver para a IA' : 'Assumir conversa'}</Button>
          {c?.status !== 'closed' && <Button variant="ghost" icon="circle-slash" onClick={encerrar} style={{ color: 'var(--danger-text)', justifyContent: 'flex-start' }}>Encerrar conversa</Button>}
        </section>
      </aside>
    </>
  );
}

const rotulo = { margin: 0, font: 'var(--type-label)', fontWeight: 'var(--fw-semibold)', color: 'var(--text-muted)', letterSpacing: 'var(--ls-caps)', textTransform: 'uppercase' };

function Balao({ m, convId }) {
  if (m.papel === 'system') {
    return (
      <div style={{ alignSelf: 'center', maxWidth: '92%', padding: '8px 12px', background: 'var(--surface-sunken)', border: '1px dashed var(--border-default)', borderRadius: 'var(--radius-control)', font: 'var(--type-caption)', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
        {m.texto}
      </div>
    );
  }
  const daIA = m.papel === 'assistant';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: daIA ? 'flex-end' : 'flex-start', gap: 3, maxWidth: '78%', alignSelf: daIA ? 'flex-end' : 'flex-start' }}>
      <div style={{ padding: m.tem_midia ? 8 : '9px 13px', background: daIA ? 'var(--accent-soft)' : 'var(--surface-raised)', border: '1px solid ' + (daIA ? 'transparent' : 'var(--border-subtle)'), borderRadius: 12, color: 'var(--text-primary)', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', lineHeight: 'var(--lh-relaxed)' }}>
        {m.tem_midia && m.midia_tipo === 'audio' ? (
          <audio controls src={`/api/conversations/${encodeURIComponent(convId)}/messages/${m.id}/media`} style={{ height: 36, maxWidth: 240 }} />
        ) : (m.texto || <span style={{ color: 'var(--text-placeholder)' }}>(sem texto)</span>)}
      </div>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, font: 'var(--type-caption)', color: 'var(--text-placeholder)' }}>
        {relativo(m.em)}
        {daIA && (m.entregue ? <Icon name="check-check" size={12} /> : <span style={{ color: 'var(--warning-text)' }}>enviando…</span>)}
      </span>
    </div>
  );
}
